import { createUuid } from '@notifi/uuid/browser';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Check,
  CloudLightning,
  CreditCard,
  Loader2,
  PauseCircle,
  ShieldCheck,
  Tags,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Label } from '@/components/atoms/label';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/atoms/sheet';
import { Textarea } from '@/components/atoms/textarea';
import { Switch } from '@/components/atoms/switch';
import { FormPageLayout } from '@/components/layouts/FormPageLayout';
import { useApiMutation, validationToastMessage } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useCreateTask } from '@/hooks/tasks';
import { useCompanyTimezone } from '@/hooks/settings/useCompanyTimezone';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import { normalizeError } from '@/lib/api/errors';
import { toastApiError } from '@/lib/api/error-toast';
import { membershipEndpoints, recurringJobEndpoints } from '@/lib/api/endpoints';
import { formatDateTimeInTimeZone } from '@/lib/date-values';
import { formatDateOnly, lastDayBefore } from '@/lib/dates/date-only';
import { jobKeys, membershipKeys } from '@/lib/queryKeys';
import { MEMBERSHIP_LIST_SEARCH } from '@/lib/memberships/membership-route-search';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { money } from '@/components/organisms/jobs/recurring/recurringDetail.helpers';
import type { Job, RecurringStatus } from '@/types/job';
import type { CancellationReasonCategory, Membership, MembershipStatus } from '@/types/membership';
import {
  isRenderableMembershipSaveOffer,
  membershipSaveOfferAmount,
} from './membership-save-offer';
import {
  isSubscriptionUnreachableError,
  refreshMembershipsWhenSubscriptionUnreachable,
} from './membership-cancel-errors';

type EntityType = 'job' | 'membership';
type CancelMode = 'AT_PERIOD_END' | 'IMMEDIATE';
type Step = 1 | 2 | 3;

type SaveOfferOutcome = 'SAVED' | 'CANCELLED_PERIOD_END' | 'CANCELLED_IMMEDIATE';

interface CancellationPreview {
  etfAmount?: number;
  effectiveDate?: string;
  willCharge?: boolean;
  feeAmount?: number;
  refundEstimate?: number;
  effectiveDateDefault?: string;
  /** Recurring jobs: where "At period end" ends the series, whatever mode is picked. */
  periodEnd?: string;
  deferredBalance?: number;
  cancellationBalancePolicy?:
    | 'ALWAYS_CREATE_INVOICE'
    | 'OPTIONAL_INVOICE'
    | 'DO_NOT_CREATE_INVOICE'
    | null;
}

interface RecordOfferInput {
  entity: EntityType;
  id: string;
  dto: {
    requestId?: string;
    offerType: string;
    offerValue?: number;
    reasonCategory?: CancellationReasonCategory;
    reasonText?: string;
    accepted: boolean;
    outcome: SaveOfferOutcome;
  };
}

interface CancelInput {
  entity: EntityType;
  id: string;
  dto: {
    mode?: CancelMode;
    path?: 'CUSTOMER_CANCEL';
    cancellationMode?: CancelMode;
    effectiveDate?: string;
    reason?: string;
    reasonCategory?: CancellationReasonCategory;
    cancelReasonCategory?: CancellationReasonCategory;
    saveOfferType?: string;
    createCancellationInvoice?: boolean;
    subscriptionReleasedElsewhere?: boolean;
  };
}

interface SaveOffer {
  type: string;
  value?: number;
  title: string;
  body: string;
  entity?: EntityType;
  requiresContactTask?: boolean;
  icon: typeof Tags;
  isAvailable?: (input: {
    entity: EntityType;
    job: Job | null;
    jobRecurringStatus: RecurringStatus | null;
    membership: Membership | null;
    membershipStatus: MembershipStatus | null;
  }) => boolean;
}

const STEP_ITEMS: Array<{ value: Step; label: string }> = [
  { value: 1, label: 'Reason' },
  { value: 2, label: 'Before you go' },
  { value: 3, label: 'Confirm' },
];

const REASON_OPTIONS: Array<{ value: CancellationReasonCategory; label: string }> = [
  { value: 'PRICE', label: 'Price' },
  { value: 'NOT_USING', label: 'Not using the service' },
  { value: 'COMPETITOR', label: 'Competitor' },
  { value: 'MOVING', label: 'Moving' },
  { value: 'BUSINESS_CLOSED', label: 'Business closed' },
  { value: 'PAYMENT_ISSUES', label: 'Payment issues' },
  { value: 'SERVICE_QUALITY', label: 'Service quality' },
  { value: 'OTHER', label: 'Other' },
];

function canPauseRecurringJob(status: RecurringStatus | null) {
  return status === 'ACTIVE';
}

function canPauseMembership(status: MembershipStatus | null) {
  return status === 'ACTIVE' || status === 'PAST_DUE';
}

const OFFERS_BY_REASON: Partial<Record<CancellationReasonCategory, SaveOffer[]>> = {
  PRICE: [
    {
      type: 'CHANGE_PLAN',
      title: 'Change plan',
      body: 'Move the customer to a better-fit membership plan.',
      entity: 'membership',
      icon: Tags,
    },
    {
      type: 'PRICE_REVIEW_TASK',
      title: 'Create price-review task',
      body: 'Assign the office to review recurring service pricing.',
      entity: 'job',
      requiresContactTask: true,
      icon: Tags,
    },
  ],
  PAYMENT_ISSUES: [
    {
      type: 'UPDATE_PAYMENT_METHOD',
      title: 'Update payment method',
      body: 'Open the customer record so the saved payment method can be repaired.',
      icon: CreditCard,
    },
    {
      type: 'PAUSE_INSTEAD',
      title: 'Pause instead',
      body: 'Keep the relationship active while the payment issue is resolved.',
      icon: PauseCircle,
      isAvailable: ({ entity, jobRecurringStatus, membershipStatus }) =>
        entity === 'job'
          ? canPauseRecurringJob(jobRecurringStatus)
          : canPauseMembership(membershipStatus),
    },
  ],
  NOT_USING: [
    {
      type: 'PAUSE_INSTEAD',
      title: 'Pause instead',
      body: 'Keep the account available without completing cancellation.',
      icon: PauseCircle,
      isAvailable: ({ entity, jobRecurringStatus, membershipStatus }) =>
        entity === 'job'
          ? canPauseRecurringJob(jobRecurringStatus)
          : canPauseMembership(membershipStatus),
    },
    {
      type: 'SCHEDULE_INCLUDED_SERVICE',
      title: 'Schedule an included service',
      body: 'Use an available membership benefit before cancelling.',
      entity: 'membership',
      icon: ShieldCheck,
      isAvailable: ({ membership }) =>
        membership?.entitlements.some(
          (entitlement) =>
            entitlement.state === 'AVAILABLE' &&
            entitlement.quantityUsed < entitlement.quantityAllowed + entitlement.quantityRolledOver,
        ) ?? false,
    },
  ],
  SERVICE_QUALITY: [
    {
      type: 'SERVICE_RECOVERY_TASK',
      title: 'Escalate to service recovery',
      body: 'Create an owner task to recover the customer.',
      requiresContactTask: true,
      icon: CloudLightning,
    },
  ],
};

function offerRequiresContact(offer: SaveOffer) {
  return offer.requiresContactTask || offer.type === 'UPDATE_PAYMENT_METHOD';
}

export function CancelSaveFlowSheet({
  entity,
  id,
  membership,
  job,
  jobRecurringStatus,
  membershipStatus,
  contactId: providedContactId,
  open,
  onOpenChange,
  onCancelled,
}: {
  entity: EntityType;
  id: string;
  membership?: Membership | null;
  job?: Job | null;
  jobRecurringStatus?: RecurringStatus | null;
  membershipStatus?: MembershipStatus | null;
  contactId?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancelled?: () => void;
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const createTask = useCreateTask({ showErrorToast: false, showSuccessToast: false });
  const companyTimeZone = useCompanyTimezone();
  const [step, setStep] = useState<Step>(1);
  const [reasonCategory, setReasonCategory] = useState<CancellationReasonCategory | ''>('');
  const [reasonText, setReasonText] = useState('');
  // A series already set to end on a date can only be ended now instead.
  const jobCancelAt = entity === 'job' ? (job?.cancelAt ?? null) : null;
  const defaultMode: CancelMode = jobCancelAt ? 'IMMEDIATE' : 'AT_PERIOD_END';
  const [mode, setMode] = useState<CancelMode>(defaultMode);
  const [lastOfferType, setLastOfferType] = useState<string | null>(null);
  const [pendingOfferType, setPendingOfferType] = useState<string | null>(null);
  const [createCancellationInvoice, setCreateCancellationInvoice] = useState(false);
  const [unreachableSubscriptionMessage, setUnreachableSubscriptionMessage] = useState<
    string | null
  >(null);
  const effectiveJobRecurringStatus = job?.recurringStatus ?? jobRecurringStatus ?? null;
  const effectiveMembershipStatus = membership?.status ?? membershipStatus ?? null;
  const membershipCurrency =
    typeof membership?.planSnapshot.priceCurrency === 'string'
      ? membership.planSnapshot.priceCurrency
      : 'USD';

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setReasonCategory('');
    setReasonText('');
    setMode(defaultMode);
    setLastOfferType(null);
    setPendingOfferType(null);
    setCreateCancellationInvoice(false);
  }, [open, defaultMode]);

  // The sheet stays mounted while closed and can be pointed at another
  // membership, so a confirmation offered for one must never carry over.
  useEffect(() => {
    setUnreachableSubscriptionMessage(null);
  }, [open, entity, id]);

  const offers = useMemo(() => {
    if (!reasonCategory) return [];
    const configuredOffers: SaveOffer[] =
      entity === 'membership' && membership
        ? (
            (membership.planSnapshot.saveOfferTemplates as
              | Array<{ type?: unknown; value?: unknown; label?: unknown }>
              | undefined) ?? []
          )
            .filter(isRenderableMembershipSaveOffer)
            .map((offer) => ({
              type: String(offer.type),
              value: membershipSaveOfferAmount(offer),
              title:
                offer.type === 'CREDIT'
                  ? `${formatCurrency(
                      membershipSaveOfferAmount(offer) ?? 0,
                      membershipCurrency,
                    )} account credit`
                  : typeof offer.label === 'string' && offer.label.trim()
                    ? offer.label
                    : 'One month free',
              body:
                offer.type === 'CREDIT'
                  ? 'Add usable credit to the customer account and keep the membership active.'
                  : 'Skip the next membership charge with no accounting entry.',
              entity: 'membership' as const,
              icon: offer.type === 'CREDIT' ? CreditCard : Tags,
            }))
        : [];
    return [...configuredOffers, ...(OFFERS_BY_REASON[reasonCategory] ?? [])].filter((offer) => {
      if (offer.entity && offer.entity !== entity) return false;
      return (
        offer.isAvailable?.({
          entity,
          job: job ?? null,
          jobRecurringStatus: effectiveJobRecurringStatus,
          membership: membership ?? null,
          membershipStatus: effectiveMembershipStatus,
        }) ?? true
      );
    });
  }, [
    effectiveJobRecurringStatus,
    effectiveMembershipStatus,
    entity,
    job,
    membership,
    membershipCurrency,
    reasonCategory,
  ]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const previewUrl =
    entity === 'membership'
      ? `${membershipEndpoints.cancellationPreview(id)}?mode=${mode}`
      : `${recurringJobEndpoints.cancellationPreview(id)}?mode=${mode}`;

  const {
    data: previewResponse,
    isLoading: previewLoading,
    isFetching: previewFetching,
    isError: previewError,
    refetch: refetchPreview,
  } = useApiQuery<CancellationPreview | { data: CancellationPreview }>({
    queryKey: ['retention', entity, id, 'cancellation-preview', mode],
    enabled: open && step === 3,
    showErrorToast: false,
    queryFn: () => apiGet(operationsClient, previewUrl),
  });

  const preview =
    previewResponse && 'data' in previewResponse ? previewResponse.data : previewResponse;

  const recordOffer = useApiMutation<unknown, RecordOfferInput>({
    showErrorToast: false,
    mutationFn: ({ entity: target, id: targetId, dto }) =>
      apiPost(
        operationsClient,
        target === 'membership'
          ? membershipEndpoints.saveOffer(targetId)
          : recurringJobEndpoints.saveOffer(targetId),
        dto,
      ),
    onSuccess: (_data, variables) => {
      if (variables.entity === 'membership') {
        void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
        void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(variables.id) });
      } else {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.id) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      }
    },
  });

  const cancelEntity = useApiMutation<unknown, CancelInput>({
    // A subscription on another Stripe account gets a confirmation step, not
    // a toast; everything else is toasted the way useApiMutation would.
    showErrorToast: false,
    onError: (error, variables) => {
      if (variables.entity === 'membership' && isSubscriptionUnreachableError(error)) {
        refreshMembershipsWhenSubscriptionUnreachable(error, queryClient);
        setUnreachableSubscriptionMessage(error.message);
        return;
      }
      if (error.code === 'CANCELED') return;
      const fieldErrors = error.fieldErrors;
      toastApiError(
        error,
        fieldErrors && fieldErrors.length > 0 ? validationToastMessage(fieldErrors) : undefined,
      );
    },
    mutationFn: ({ entity: target, id: targetId, dto }) =>
      apiPost(
        operationsClient,
        target === 'membership'
          ? membershipEndpoints.cancel(targetId)
          : recurringJobEndpoints.cancel(targetId),
        dto,
      ),
    onSuccess: (data, variables) => {
      if (variables.entity === 'membership') {
        void queryClient.invalidateQueries({ queryKey: membershipKeys.all() });
        void queryClient.invalidateQueries({ queryKey: membershipKeys.detail(variables.id) });
      } else {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(variables.id) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      }
      const endsAt = variables.entity === 'job' ? scheduledEnd(data) : null;
      toast.success(
        endsAt
          ? `${entityLabel} ends on ${formatContractDate(endsAt, companyTimeZone)}`
          : `${entityLabel} cancelled`,
      );
      onCancelled?.();
      onOpenChange(false);
    },
  });

  const entityLabel = entity === 'membership' ? 'Membership' : 'Service';
  const membershipPreviewPeriodEnd =
    preview?.effectiveDateDefault ??
    (mode === 'AT_PERIOD_END' ? preview?.effectiveDate : undefined);
  const prepaidMembership = entity === 'membership' && membership?.collectionMethod === 'PREPAID';
  const prepaidPeriodEndUnconfirmed =
    prepaidMembership &&
    (previewLoading || previewFetching || previewError || !membershipPreviewPeriodEnd);
  const prepaidPeriodEndUnavailable =
    prepaidPeriodEndUnconfirmed && !previewLoading && !previewFetching;
  // A series ends at the end of what was billed (its billing period, or a
  // prepaid term), not at its contract end date.
  const periodEnd =
    entity === 'membership'
      ? (membershipPreviewPeriodEnd ?? membership?.currentPeriodEnd)
      : (preview?.periodEnd ??
        (mode === 'AT_PERIOD_END' ? preview?.effectiveDateDefault : undefined));
  const periodEndRequired = mode === 'AT_PERIOD_END' && (entity === 'job' || prepaidMembership);
  const periodEndPending = periodEndRequired && (!periodEnd || prepaidPeriodEndUnconfirmed);
  const shownFee = preview?.feeAmount ?? preview?.etfAmount ?? 0;
  const shownRefund = preview?.refundEstimate ?? 0;
  const contextRows = buildContextRows(entity, membership ?? null, job ?? null, companyTimeZone);
  const actionPending =
    recordOffer.isPending ||
    createTask.isPending ||
    cancelEntity.isPending ||
    Boolean(pendingOfferType);
  const hasUnsavedWork =
    open &&
    !actionPending &&
    (step !== 1 || mode !== defaultMode || Boolean(reasonCategory) || reasonText.trim().length > 0);
  const flowTitle = entity === 'membership' ? 'Cancel membership' : 'Cancel recurring service';
  const flowSubtitle = buildSubtitle(entity, id, membership ?? null, job ?? null);
  const contactId =
    providedContactId ?? (entity === 'membership' ? membership?.customerId : job?.contactId);
  const selectedReasonLabel =
    REASON_OPTIONS.find((option) => option.value === reasonCategory)?.label ?? '';

  useEffect(() => {
    if (!hasUnsavedWork) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedWork]);

  function requestClose() {
    if (actionPending) return;
    if (
      hasUnsavedWork &&
      !window.confirm(
        'Leave this cancellation flow? The selected reason and notes will be discarded.',
      )
    ) {
      return;
    }
    onOpenChange(false);
  }

  function continueFromReason() {
    if (!reasonCategory) return;
    setStep(offers.length > 0 ? 2 : 3);
  }

  async function acceptOffer(offer: SaveOffer) {
    if (!reasonCategory) return;
    if (offerRequiresContact(offer) && !contactId) {
      toast.error('A customer is required to apply this save option.');
      return;
    }
    setPendingOfferType(offer.type);
    const requestStorageKey = [
      'retention-offer-request',
      entity,
      id,
      offer.type,
      String(offer.value ?? ''),
      encodeURIComponent(offer.title),
    ].join(':');
    const requestId = window.sessionStorage.getItem(requestStorageKey) ?? createUuid();
    window.sessionStorage.setItem(requestStorageKey, requestId);
    try {
      if (offer.type === 'CHANGE_PLAN' && entity === 'membership') {
        window.sessionStorage.setItem(
          `membership-retention-offer:${id}`,
          JSON.stringify({
            offerType: offer.type,
            reasonCategory,
            reasonText: reasonText.trim() || undefined,
          }),
        );
        onOpenChange(false);
        void router.navigate({
          to: '/dashboard/memberships/$id/edit',
          params: { id },
          search: MEMBERSHIP_LIST_SEARCH,
        });
        toast.info('Choose a different plan and save to complete the retention offer.');
        return;
      }
      if (offer.type === 'UPDATE_PAYMENT_METHOD' && entity === 'membership') {
        window.sessionStorage.setItem(
          `membership-retention-offer:${id}`,
          JSON.stringify({
            offerType: offer.type,
            reasonCategory,
            reasonText: reasonText.trim() || undefined,
          }),
        );
        onOpenChange(false);
        void router.navigate({
          to: '/dashboard/memberships/$id/edit',
          params: { id },
          search: MEMBERSHIP_LIST_SEARCH,
        });
        toast.info('Choose a different saved payment method and save to complete the offer.');
        return;
      }
      if (offer.type === 'UPDATE_PAYMENT_METHOD' && contactId) {
        onOpenChange(false);
        void router.navigate({
          to: '/dashboard/contacts/$contactId',
          params: { contactId },
          search: {
            tab: 'payments',
            paymentAction: 'add-card',
            paymentActionToken: String(Date.now()),
            retentionEntity: entity,
            retentionId: id,
            retentionOfferType: offer.type,
            retentionReasonCategory: reasonCategory,
            retentionReasonText: reasonText.trim() || undefined,
          } as never,
        });
        toast.info('Save a payment method to complete the retention offer.');
        return;
      }
      if (offer.type === 'SCHEDULE_INCLUDED_SERVICE' && entity === 'membership' && contactId) {
        const entitlement = membership?.entitlements.find(
          (candidate) =>
            candidate.state === 'AVAILABLE' &&
            candidate.quantityUsed < candidate.quantityAllowed + candidate.quantityRolledOver,
        );
        if (!entitlement) {
          toast.error('No available membership benefit could be scheduled.');
          return;
        }
        onOpenChange(false);
        void router.navigate({
          to: '/dashboard/bookings/new',
          search: {
            contactId,
            retentionEntity: entity,
            retentionId: id,
            retentionOfferType: offer.type,
            retentionEntitlementId: entitlement.entitlementId,
            retentionReasonCategory: reasonCategory,
            retentionReasonText: reasonText.trim() || undefined,
          } as never,
        });
        toast.info('Create the service request to complete the retention offer.');
        return;
      }
      if (offer.type === 'PRICE_REVIEW_TASK' || offer.type === 'SERVICE_RECOVERY_TASK') {
        await createTask.mutateAsync({
          contactId: contactId!,
          jobId: entity === 'job' ? id : undefined,
          title:
            offer.type === 'PRICE_REVIEW_TASK'
              ? 'Review recurring service price'
              : 'Service recovery follow-up',
          description: reasonText.trim() || offer.body,
          priority: offer.type === 'SERVICE_RECOVERY_TASK' ? 'urgent' : 'high',
        });
      }
      if (offer.type === 'PAUSE_INSTEAD') {
        if (entity === 'membership') {
          if (!canPauseMembership(effectiveMembershipStatus)) {
            toast.error('This membership cannot be paused from its current status.');
            return;
          }
          await apiPost(operationsClient, membershipEndpoints.suspend(id), {
            reason: reasonText.trim() || 'Customer accepted pause instead of cancellation',
          });
        } else {
          if (!canPauseRecurringJob(effectiveJobRecurringStatus)) {
            toast.error('This recurring service cannot be paused from its current status.');
            return;
          }
          const pauseWindow = defaultPauseWindow();
          await apiPost(operationsClient, recurringJobEndpoints.pause(id), {
            pauseStart: pauseWindow.pauseStart,
            pauseEnd: pauseWindow.pauseEnd,
            billingPolicy: 'CONTINUE',
          });
        }
      }
      await recordOffer.mutateAsync({
        entity,
        id,
        dto: {
          requestId: entity === 'membership' ? requestId : undefined,
          offerType: offer.type,
          // Membership credits are plan-authoritative. Omitting the displayed
          // value avoids JS/accounting rounding differences for legacy plans;
          // the backend applies the frozen plan snapshot amount.
          offerValue: entity === 'membership' && offer.type === 'CREDIT' ? undefined : offer.value,
          reasonCategory,
          reasonText: reasonText.trim() || undefined,
          accepted: true,
          outcome: 'SAVED',
        },
      });

      window.sessionStorage.removeItem(requestStorageKey);
      setLastOfferType(offer.type);
      onOpenChange(false);
      toast.success(`Cancellation avoided - ${offer.title}`);
    } catch (err) {
      const error = normalizeError(err);
      // The pause and the save offer both reach the Stripe subscription.
      if (entity === 'membership') {
        refreshMembershipsWhenSubscriptionUnreachable(error, queryClient);
      }
      toast.error(error.message || 'Could not apply this option');
    } finally {
      setPendingOfferType(null);
    }
  }

  function confirmCancel(subscriptionReleasedElsewhere = false) {
    if (!reasonCategory) return;
    if (periodEndPending) {
      toast.error(
        prepaidPeriodEndUnavailable
          ? 'Period end is unavailable. Retry the preview.'
          : 'Period end is still loading.',
      );
      return;
    }
    if (!subscriptionReleasedElsewhere) setUnreachableSubscriptionMessage(null);
    cancelEntity.mutate({
      entity,
      id,
      dto:
        entity === 'membership'
          ? {
              mode,
              reason: reasonText.trim() || undefined,
              reasonCategory,
              saveOfferType: lastOfferType ?? undefined,
              createCancellationInvoice,
              subscriptionReleasedElsewhere: subscriptionReleasedElsewhere || undefined,
            }
          : {
              path: 'CUSTOMER_CANCEL',
              cancellationMode: mode,
              effectiveDate: mode === 'AT_PERIOD_END' ? (periodEnd ?? undefined) : undefined,
              reason: reasonText.trim() || undefined,
              cancelReasonCategory: reasonCategory,
              saveOfferType: lastOfferType ?? undefined,
            },
    });
  }

  if (!open) return null;

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
          return;
        }
        requestClose();
      }}
    >
      <SheetContent
        side="right"
        showCloseButton={false}
        className="inset-0 h-full w-full max-w-none gap-0 border-0 p-0 sm:max-w-none"
      >
        <SheetTitle className="sr-only">{flowTitle}</SheetTitle>
        <SheetDescription className="sr-only">{flowSubtitle}</SheetDescription>
        <FormPageLayout title={flowTitle} subtitle={flowSubtitle} onBack={requestClose}>
          <div className="mx-auto w-full max-w-[760px] space-y-6 px-4 py-5 sm:px-6">
            <CancelStepper step={step} />

            {step === 1 && (
              <section className="space-y-5">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">Why are you cancelling?</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    This helps us improve - and might surface a better option than cancelling.
                  </p>
                </div>

                <RadioGroup
                  value={reasonCategory}
                  onValueChange={(value) => setReasonCategory(value as CancellationReasonCategory)}
                  className="space-y-2"
                >
                  {REASON_OPTIONS.map((option) => {
                    const selected = reasonCategory === option.value;
                    return (
                      <Label
                        key={option.value}
                        className={cn(
                          'flex min-h-12 cursor-pointer items-center gap-3 rounded-md border bg-card px-4 py-3 text-sm transition-colors hover:bg-muted/40',
                          selected && 'border-foreground bg-muted/30 ring-2 ring-foreground/10',
                        )}
                      >
                        <RadioGroupItem value={option.value} />
                        <span className="font-medium">{option.label}</span>
                      </Label>
                    );
                  })}
                </RadioGroup>

                <div className="space-y-2">
                  <Label>Anything else? (optional)</Label>
                  <Textarea
                    value={reasonText}
                    onChange={(event) => setReasonText(event.target.value)}
                    rows={4}
                    placeholder="Add cancellation context..."
                  />
                </div>

                <FlowFooter>
                  <Button type="button" variant="ghost" onClick={requestClose}>
                    Never mind, keep it
                  </Button>
                  <Button type="button" onClick={continueFromReason} disabled={!reasonCategory}>
                    Continue
                    <ArrowRight className="size-4" />
                  </Button>
                </FlowFooter>
              </section>
            )}

            {step === 2 && (
              <section className="space-y-5">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">Before you go</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    You told us it is about {selectedReasonLabel.toLowerCase()}. Here is something
                    that might help.
                  </p>
                </div>

                <div className="space-y-3 rounded-md border bg-muted/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    This customer at a glance
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {contextRows.map((row) => (
                      <div key={row.label} className="rounded-md border bg-card px-3 py-2.5">
                        <p className="text-xs text-muted-foreground">{row.label}</p>
                        <p
                          className={cn(
                            'mt-0.5 text-sm font-semibold',
                            row.tone === 'warning' && 'text-amber-700',
                          )}
                        >
                          {row.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  {offers.map((offer) => {
                    const Icon = offer.icon;
                    const disabled = offerRequiresContact(offer) && !contactId;
                    const pending = pendingOfferType === offer.type;
                    return (
                      <div
                        key={offer.type}
                        className="flex flex-col gap-3 rounded-md border bg-card p-4 sm:flex-row sm:items-start sm:justify-between"
                      >
                        <div className="flex min-w-0 gap-3">
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-blue-50 text-blue-700">
                            <Icon className="size-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">{offer.title}</p>
                            <p className="mt-0.5 text-sm text-muted-foreground">{offer.body}</p>
                            {disabled && (
                              <p className="mt-1 text-xs text-destructive">
                                Customer link required.
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="w-full sm:w-auto"
                          disabled={actionPending || disabled}
                          onClick={() => void acceptOffer(offer)}
                        >
                          {pending && <Loader2 className="size-3.5 animate-spin" />}
                          Apply
                        </Button>
                      </div>
                    );
                  })}
                </div>

                <FlowFooter>
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="size-4" />
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep(3)}
                    disabled={actionPending}
                  >
                    None of these - continue cancelling
                  </Button>
                </FlowFooter>
              </section>
            )}

            {step === 3 && (
              <section className="space-y-5">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight">Confirm cancellation</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Review the effective date and any money impact before you cancel.
                  </p>
                </div>

                <div className="space-y-4 rounded-md border border-destructive/30 bg-destructive/10 p-4">
                  <div className="flex gap-3 rounded-md border border-destructive/20 bg-background/70 p-3 text-sm text-destructive">
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <p>
                      {entity === 'membership' ? (
                        <>
                          <span className="font-semibold">This cancels the membership.</span>{' '}
                          Benefits stop at the effective date and the membership contract closes.
                          This cannot be undone.
                        </>
                      ) : mode === 'AT_PERIOD_END' ? (
                        <>
                          <span className="font-semibold">
                            This ends the recurring series
                            {periodEnd
                              ? ` after ${formatDateOnly(lastDayBefore(periodEnd))}`
                              : ' at period end'}
                            .
                          </span>{' '}
                          Its visits and billing continue until then, and it will not renew. Until
                          that date you can keep the series from its plan card.
                        </>
                      ) : (
                        <>
                          <span className="font-semibold">This ends the recurring series now.</span>{' '}
                          Future visits are cancelled and the contract closes. This cannot be
                          undone.
                        </>
                      )}
                    </p>
                  </div>

                  <RadioGroup
                    value={mode}
                    onValueChange={(value) => setMode(value as CancelMode)}
                    className="space-y-2"
                  >
                    <Label
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-md border bg-card p-3 text-sm transition-colors',
                        jobCancelAt
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:bg-background',
                        mode === 'AT_PERIOD_END' && 'border-destructive ring-2 ring-destructive/15',
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <RadioGroupItem value="AT_PERIOD_END" disabled={Boolean(jobCancelAt)} />
                        <span className="font-medium">At period end</span>
                      </span>
                      <span className="text-muted-foreground">
                        {jobCancelAt
                          ? `Ends after ${formatDateOnly(lastDayBefore(jobCancelAt))}`
                          : prepaidPeriodEndUnconfirmed
                            ? prepaidPeriodEndUnavailable
                              ? 'Unavailable'
                              : 'Loading...'
                            : periodEnd
                              ? entity === 'job'
                                ? // The series' last day: it ends at the midnight after it.
                                  formatDateOnly(lastDayBefore(periodEnd))
                                : formatContractDate(periodEnd, companyTimeZone)
                              : previewLoading
                                ? 'Loading...'
                                : 'Unavailable'}
                      </span>
                    </Label>
                    <Label
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-md border bg-card p-3 text-sm transition-colors hover:bg-background',
                        mode === 'IMMEDIATE' && 'border-destructive ring-2 ring-destructive/15',
                      )}
                    >
                      <RadioGroupItem value="IMMEDIATE" />
                      <span className="font-medium">Immediately</span>
                    </Label>
                  </RadioGroup>
                  {mode === 'AT_PERIOD_END' && prepaidPeriodEndUnavailable ? (
                    <div className="space-y-2 text-sm" role="alert">
                      <p>Could not confirm the membership period end.</p>
                      <Button type="button" variant="outline" onClick={() => void refetchPreview()}>
                        Retry preview
                      </Button>
                    </div>
                  ) : null}

                  <div className="space-y-2 rounded-md border bg-card p-3 text-sm">
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Cancellation fee</span>
                      <span className="font-medium">{money(shownFee, true)}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span className="text-muted-foreground">Refund estimate</span>
                      <span className="font-medium">{money(shownRefund, true)}</span>
                    </div>
                    {entity === 'membership' && (preview?.deferredBalance ?? 0) > 0 ? (
                      <>
                        <div className="flex justify-between gap-3 border-t border-border pt-2">
                          <span className="text-muted-foreground">Remaining deferred revenue</span>
                          <span className="font-medium">
                            {money(preview?.deferredBalance ?? 0, true)}
                          </span>
                        </div>
                        {preview?.cancellationBalancePolicy === 'OPTIONAL_INVOICE' ? (
                          <label className="flex items-center justify-between gap-4 rounded-md border border-border p-3">
                            <span>
                              <span className="block font-medium">
                                Create charge/refund invoice
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                Creates a zero-balance audit invoice and clears the deferred
                                balance.
                              </span>
                            </span>
                            <Switch
                              checked={createCancellationInvoice}
                              onCheckedChange={setCreateCancellationInvoice}
                            />
                          </label>
                        ) : preview?.cancellationBalancePolicy === 'ALWAYS_CREATE_INVOICE' ? (
                          <p className="text-xs text-muted-foreground">
                            A zero-balance charge/refund invoice will be created automatically.
                          </p>
                        ) : null}
                      </>
                    ) : null}
                    {preview?.willCharge ? (
                      <p className="text-xs text-muted-foreground">
                        The fee will be charged to the saved payment method.
                      </p>
                    ) : shownFee > 0 ? (
                      <p className="text-xs text-amber-700">
                        No saved payment method is available. This fee will not be charged
                        automatically.
                      </p>
                    ) : null}
                  </div>
                </div>

                {unreachableSubscriptionMessage ? (
                  <div
                    role="alert"
                    className="flex gap-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  >
                    <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                    <div className="space-y-2">
                      <p>{unreachableSubscriptionMessage}</p>
                      <p>
                        Once it is cancelled there, you can cancel the membership here. Stripe will
                        not be contacted again for it.
                      </p>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => confirmCancel(true)}
                        disabled={cancelEntity.isPending}
                      >
                        I cancelled it in Stripe — cancel membership
                      </Button>
                    </div>
                  </div>
                ) : null}

                <FlowFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(offers.length ? 2 : 1)}
                  >
                    <ArrowLeft className="size-4" />
                    Back
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => confirmCancel()}
                    disabled={cancelEntity.isPending || periodEndPending}
                  >
                    {cancelEntity.isPending && <Loader2 className="size-3.5 animate-spin" />}
                    {periodEndPending
                      ? prepaidPeriodEndUnavailable
                        ? 'Period end unavailable'
                        : 'Loading period end...'
                      : `Cancel ${entityLabel.toLowerCase()}`}
                  </Button>
                </FlowFooter>
              </section>
            )}
          </div>
        </FormPageLayout>
      </SheetContent>
    </Sheet>
  );
}

function buildContextRows(
  entity: EntityType,
  membership: Membership | null,
  job: Job | null,
  timeZone?: string,
): Array<{ label: string; value: string; tone?: 'warning' }> {
  if (entity === 'membership' && membership) {
    const used = (membership.entitlements ?? []).reduce(
      (sum, entitlement) => sum + Number(entitlement.quantityUsed ?? 0),
      0,
    );
    const hasFailedPayments = Number(membership.failedPaymentCount ?? 0) > 0;
    return [
      {
        label: 'Customer since',
        value: formatDate(membership.activatedAt ?? membership.createdAt, timeZone),
      },
      { label: 'Benefits used', value: String(used) },
      {
        label: 'Payment history',
        value: hasFailedPayments ? 'Failed payments' : 'OK',
        tone: hasFailedPayments ? 'warning' : undefined,
      },
    ];
  }
  if (job) {
    const hasFailedPayments =
      job.recurringStatus === 'PAST_DUE' || job.recurringStatus === 'SUSPENDED';
    return [
      { label: 'Customer since', value: formatDate(job.createdAt, timeZone) },
      {
        label: 'Visits generated',
        value:
          typeof job.recurringConfig?.visitsGenerated === 'number'
            ? String(job.recurringConfig.visitsGenerated)
            : '-',
      },
      {
        label: 'Payment history',
        value: hasFailedPayments ? 'Failed payments' : 'OK',
        tone: hasFailedPayments ? 'warning' : undefined,
      },
    ];
  }
  return [
    { label: 'Customer since', value: '-' },
    { label: 'Usage', value: '-' },
    { label: 'Payment history', value: '-' },
  ];
}

function buildSubtitle(
  entity: EntityType,
  id: string,
  membership: Membership | null,
  job: Job | null,
): string {
  if (entity === 'job') {
    const customer =
      job?.billToContact?.contactName ??
      job?.billToContact?.name ??
      job?.serviceContact?.name ??
      'Customer';
    return `${customer} · ${job?.jobNumber ?? id}`;
  }
  return `Membership ${membership?.membershipNumber ?? id}`;
}

function CancelStepper({ step }: { step: Step }) {
  return (
    <div className="flex flex-wrap items-center gap-y-2">
      {STEP_ITEMS.map((item, index) => {
        const done = item.value < step;
        const active = item.value === step;
        return (
          <div key={item.value} className="flex items-center">
            <div
              className={cn(
                'flex items-center gap-2 text-sm',
                done && 'text-foreground',
                active && 'font-semibold text-foreground',
                !done && !active && 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'flex size-7 items-center justify-center rounded-full border text-xs font-semibold',
                  done && 'border-emerald-600 bg-emerald-600 text-white',
                  active && 'border-foreground bg-foreground text-background',
                  !done && !active && 'border-border bg-background text-muted-foreground',
                )}
              >
                {done ? <Check className="size-3.5" /> : item.value}
              </span>
              {item.label}
            </div>
            {index < STEP_ITEMS.length - 1 && (
              <span className="mx-3 h-px w-8 bg-border sm:mx-4 sm:w-14" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function FlowFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col-reverse gap-2 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
      {children}
    </div>
  );
}

function formatDate(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return formatDateTimeInTimeZone(value, timeZone, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** The end date a recurring cancel set for later answers with, if it did. */
function scheduledEnd(response: unknown): string | null {
  const body =
    response && typeof response === 'object' && 'data' in response
      ? (response as { data: unknown }).data
      : response;
  const cancelAt =
    body && typeof body === 'object' ? (body as { cancelAt?: unknown }).cancelAt : null;
  return typeof cancelAt === 'string' ? cancelAt : null;
}

function formatContractDate(value: string, timeZone?: string): string {
  const dateOnly = value.match(/^\d{4}-\d{2}-\d{2}/)?.[0];
  return dateOnly ? formatDateOnly(dateOnly) : formatDate(value, timeZone);
}

function defaultPauseWindow(): { pauseStart: string; pauseEnd: string } {
  const pauseStart = new Date();
  const pauseEnd = new Date(pauseStart.getTime() + 30 * 24 * 60 * 60 * 1000);
  return {
    pauseStart: pauseStart.toISOString(),
    pauseEnd: pauseEnd.toISOString(),
  };
}
